import { useLocalSearchParams, useNavigation, useRouter } from 'expo-router'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { FlatList, StyleSheet, View } from 'react-native'
import { Divider, IconButton } from 'react-native-paper'
import { DEFAULT_PERIOD_TYPE, DEFAULT_WEEK_START_DAY } from '@/constants/periods'
import { useActiveEntry } from '@/hooks/useActiveEntry'
import { useAutoGeofenceOnForeground } from '@/hooks/useAutoGeofenceOnForeground'
import { useConnectivityMonitor } from '@/hooks/useConnectivityMonitor'
import { useCurrentOrg } from '@/hooks/useCurrentOrg'
import { useExportTimeEntries } from '@/hooks/useExportTimeEntries'
import { useHeaderOptions } from '@/hooks/useHeaderOptions'
import { useOrgSettings } from '@/hooks/useOrgSettings'
import { usePeriodEntries } from '@/hooks/usePeriodEntries'
import { usePeriodLocked } from '@/hooks/usePeriodLocked'
import { usePeriodNavigation } from '@/hooks/usePeriodNavigation'
import { type DayGroup, groupEntriesByDate } from '@/lib/group-entries-by-date'
import { useNoticeWhile } from '@/lib/notice'
import type { PeriodConfig, WeekStartDay } from '@/types/periods'
import type { TimeEntriesExpanded } from '@/types/timeTracking'
import { useCollectionRefresh } from '@/ui/CollectionRefreshControl'
import { EmptyCard } from '@/ui/EmptyCard'
import { ListWrapper } from '@/ui/ListWrapper'
import { LoadingIndicator } from '@/ui/LoadingIndicator'
import { PeriodNavigator } from '@/ui/PeriodNavigator'
import { ActiveTimer } from '@/ui/time-entry/ActiveTimer'
import { AuditHistoryDialog } from '@/ui/time-entry/AuditHistoryDialog'
import { ClockInBreakTabs } from '@/ui/time-entry/ClockInBreakTabs'
import { DailyActivityCard } from '@/ui/time-entry/DailyActivityCard'
import { ManualEntryDialog } from '@/ui/time-entry/ManualEntryDialog'
import { PeriodTotal } from '@/ui/time-entry/PeriodTotal'

function PeriodSection({
    isVisible,
    periodLabel,
    onPrevious,
    onNext,
    onExport,
    exportDisabled,
    isExporting,
    periodDate,
    onDatePicked,
}: {
    isVisible: boolean
    periodLabel: string
    onPrevious: () => void
    onNext: () => void
    onExport: () => void
    exportDisabled: boolean
    isExporting: boolean
    periodDate: Date
    onDatePicked: (date: Date) => void
}) {
    if (!isVisible) return null

    return (
        <View style={styles.periodSection}>
            <View style={styles.periodNavigatorWrapper}>
                <PeriodNavigator
                    periodLabel={periodLabel}
                    onPrevious={onPrevious}
                    onNext={onNext}
                    periodDate={periodDate}
                    onDatePicked={onDatePicked}
                />
            </View>
            <IconButton
                icon="download"
                size={20}
                onPress={onExport}
                disabled={exportDisabled}
                loading={isExporting}
            />
        </View>
    )
}

const TimerSection = React.memo(function TimerSection({
    activeEntry,
    activeBreakEntry,
    orgSlug,
    isLocked,
}: {
    activeEntry: TimeEntriesExpanded | null
    activeBreakEntry: TimeEntriesExpanded | null
    orgSlug: string
    isLocked: boolean
}) {
    const hasActiveTimer = Boolean(activeEntry || activeBreakEntry)

    return (
        <View style={styles.section}>
            {hasActiveTimer ? (
                <ActiveTimer entry={activeEntry} breakEntry={activeBreakEntry} />
            ) : (
                <ClockInBreakTabs orgSlug={orgSlug} isLocked={isLocked} />
            )}
        </View>
    )
})

export default function EntryPage() {
    const params = useLocalSearchParams<{ orgSlug: string }>()
    const navigation = useNavigation()
    const router = useRouter()

    const { isConnected } = useConnectivityMonitor()
    const { activeEntry, activeBreakEntry } = useActiveEntry()
    useAutoGeofenceOnForeground(params.orgSlug)
    const { refreshControl, isSyncing } = useCollectionRefresh(['time_entries'], {
        refreshOnMount: true,
    })
    const [showManualEntry, setShowManualEntry] = useState(false)
    const [editEntry, setEditEntry] = useState<TimeEntriesExpanded | null>(null)
    const [historyEntryId, setHistoryEntryId] = useState<string | null>(null)
    const [expandedDays, setExpandedDays] = useState<Set<string>>(() => {
        const now = new Date()
        const y = now.getFullYear()
        const m = String(now.getMonth() + 1).padStart(2, '0')
        const d = String(now.getDate()).padStart(2, '0')
        return new Set([`${y}-${m}-${d}`])
    })
    const { role } = useCurrentOrg({ throwIfNotFound: false })
    const { settings } = useOrgSettings(params.orgSlug)

    const periodConfig: PeriodConfig = useMemo(
        () => ({
            periodType: settings?.period_type || DEFAULT_PERIOD_TYPE,
            weekStartDay: (settings?.week_start_day ?? DEFAULT_WEEK_START_DAY) as WeekStartDay,
        }),
        [settings?.period_type, settings?.week_start_day]
    )

    const {
        periodDate,
        handlePrevious: handlePreviousPeriod,
        handleNext: handleNextPeriod,
        handleDatePicked,
        isCurrentPeriod,
    } = usePeriodNavigation(periodConfig)

    useNoticeWhile(isSyncing && isCurrentPeriod && isConnected !== false, {
        id: 'syncing',
        severity: 'info',
        icon: 'spinner',
        message: 'Fetching latest entry and location information',
        dismissible: false,
    })

    const { entries, completedHours, periodLabel, periodStart, periodEnd, isLoading } =
        usePeriodEntries({
            periodDate,
            periodConfig,
        })

    const entriesLockedUntil =
        (settings as unknown as { entries_locked_until?: string } | null)?.entries_locked_until ||
        null
    const { isPeriodLocked, isDateLocked } = usePeriodLocked({
        entriesLockedUntil,
        periodStart,
        periodEnd,
    })

    const isWorkforce = role === 'workforce'
    const periodIsLockedForUser = isWorkforce && isPeriodLocked

    const isEntryLocked = useCallback(
        (entry: TimeEntriesExpanded) => {
            if (!isWorkforce) return false
            return isDateLocked(entry.clock_in)
        },
        [isWorkforce, isDateLocked]
    )

    const dayGroups = useMemo(() => groupEntriesByDate(entries), [entries])

    const { exportToCSV, isExporting } = useExportTimeEntries({
        periodLabel,
        entries,
        includeUser: false,
    })

    useEffect(() => {
        if (role === 'workforce') {
            navigation.setOptions({
                headerLeft: () => (
                    <IconButton
                        icon="cog-outline"
                        size={22}
                        onPress={() => router.push(`/${params.orgSlug}/settings`)}
                    />
                ),
            })
        } else if (isConnected === false) {
            navigation.setOptions({
                headerBackVisible: false,
                headerLeft: () => null,
            })
        } else {
            navigation.setOptions({
                headerBackVisible: undefined,
                headerLeft: undefined,
            })
        }
    }, [role, navigation, router, params.orgSlug, isConnected])

    const isSyncingWhileOnline = isSyncing && isConnected !== false
    useHeaderOptions({
        buttons: {
            type: 'create',
            title: 'Add Entry',
            onPress: () => setShowManualEntry(true),
            disabled: isSyncingWhileOnline,
        },
        enabled: !periodIsLockedForUser,
    })

    const toggleDay = useCallback((dateKey: string) => {
        setExpandedDays(prev => {
            const next = new Set(prev)
            if (next.has(dateKey)) {
                next.delete(dateKey)
            } else {
                next.add(dateKey)
            }
            return next
        })
    }, [])

    const handleViewHistory = useCallback((entry: TimeEntriesExpanded) => {
        setHistoryEntryId(entry.id)
    }, [])

    const handleEditEntry = useCallback((entry: TimeEntriesExpanded) => {
        setEditEntry(entry)
    }, [])

    const renderHeader = useCallback(
        () => (
            <View>
                <TimerSection
                    activeEntry={activeEntry}
                    activeBreakEntry={activeBreakEntry}
                    orgSlug={params.orgSlug}
                    isLocked={periodIsLockedForUser}
                />
                <Divider style={styles.divider} />
                <PeriodSection
                    isVisible={isConnected !== false}
                    periodLabel={periodLabel}
                    onPrevious={handlePreviousPeriod}
                    onNext={handleNextPeriod}
                    onExport={exportToCSV}
                    exportDisabled={entries.length === 0 || isExporting}
                    isExporting={isExporting}
                    periodDate={periodDate}
                    onDatePicked={handleDatePicked}
                />
            </View>
        ),
        [
            activeEntry,
            activeBreakEntry,
            params.orgSlug,
            periodIsLockedForUser,
            isConnected,
            periodLabel,
            entries,
            handleNextPeriod,
            handlePreviousPeriod,
            handleDatePicked,
            periodDate,
            exportToCSV,
            isExporting,
        ]
    )

    const renderItem = useCallback(
        ({ item }: { item: DayGroup }) => (
            <DailyActivityCard
                dayGroup={item}
                onEditEntry={handleEditEntry}
                isAdmin={false}
                showApprove={false}
                showEdit
                onViewHistory={handleViewHistory}
                expanded={expandedDays.has(item.dateKey)}
                onToggleExpanded={() => toggleDay(item.dateKey)}
                isEntryLocked={isEntryLocked}
            />
        ),
        [handleEditEntry, handleViewHistory, expandedDays, toggleDay, isEntryLocked]
    )

    const renderEmpty = () => {
        if (isLoading && isConnected !== false) {
            return <LoadingIndicator />
        }
        if (isConnected === false) {
            return (
                <EmptyCard
                    icon="wifi-off"
                    title="You're currently offline"
                    subtitle="Some entries may not be shown until you're back online"
                />
            )
        }
        return (
            <EmptyCard
                icon="timetable"
                title={`No time entries ${periodLabel}`}
                subtitle="Clock in to start tracking your time"
            />
        )
    }

    return (
        <ListWrapper>
            <FlatList
                data={dayGroups}
                keyExtractor={item => item.dateKey}
                renderItem={renderItem}
                ListHeaderComponent={renderHeader}
                ListEmptyComponent={renderEmpty}
                ListFooterComponent={
                    entries.length > 0 ? <PeriodTotal totalHours={completedHours} /> : null
                }
                style={styles.list}
                contentContainerStyle={styles.listContent}
                refreshControl={refreshControl}
            />
            {params.orgSlug && (
                <ManualEntryDialog
                    visible={showManualEntry}
                    orgSlug={params.orgSlug}
                    onDismiss={() => setShowManualEntry(false)}
                    entriesLockedUntil={entriesLockedUntil}
                />
            )}
            <ManualEntryDialog
                visible={editEntry !== null}
                orgSlug={params.orgSlug}
                entry={editEntry}
                onDismiss={() => setEditEntry(null)}
                entriesLockedUntil={entriesLockedUntil}
            />
            <AuditHistoryDialog
                visible={historyEntryId !== null}
                timeEntryId={historyEntryId ?? ''}
                onDismiss={() => setHistoryEntryId(null)}
            />
        </ListWrapper>
    )
}

const styles = StyleSheet.create({
    section: {
        paddingHorizontal: 16,
        paddingVertical: 16,
    },
    periodSection: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingHorizontal: 16,
        paddingVertical: 8,
    },
    periodNavigatorWrapper: {
        flex: 1,
    },
    divider: {},
    list: {
        flex: 1,
    },
    listContent: {
        flexGrow: 1,
        maxWidth: 1000,
        width: '100%',
        alignSelf: 'center',
    },
})
